
import { Component, Provide, Mixins, Prop } from "vue-property-decorator";
import { CreateElement } from "vue";

// Mixins
import RequestPromises from "@/mixins/requestPromises";

// Enums/Data
import { DataModules } from "@/store/modules/data";

// Types
import {
  IReportProviderData,
  IReportProviderMethods
} from "@/models/providers/reports";
import { IReportType } from "@/models/reports";

@Component
export default class ReportProvider extends Mixins(RequestPromises) {
  // Props

  @Prop({ required: true })
  public declare reportCode: IReportType["code"];

  @Prop({ default: true })
  public declare handleLoading: boolean;

  @Prop({ default: true })
  public declare handleGuarding: boolean;

  @Prop({ default: true })
  public declare handleRequestError: boolean;

  // Data

  public reportHelp: any = {};
  public guardFunctionalities = ["reports_help"];

  public isLoading = true;
  public isProcessing = false;
  public isReloading = false;
  public requestError = false;

  public filters = {};

  // Life-cycle methods

  public async created() {
    if (!this.contentIsAvailable) {
      this.isLoading = false;
      return;
    }

    this.loadData();
  }

  // Methods

  public async loadData() {
    try {
      this.reportHelp = await this.getReportHelp();
      this.requestError = false;
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public getReportHelp() {
    return this.$store.dispatch(`data/${DataModules.REPORTS}/getReportHelp`, {
      reportCode: this.reportCode,
      storeData: false,
      returnData: true,
      vm: this
    });
  }

  public async createReport() {
    try {
      this.isProcessing = true;
      await this.$store.dispatch(`data/${DataModules.REPORTS}/createReport`, {
        reportCode: this.reportCode,
        data: this.filters,
        vm: this
      });
      this.$store.dispatch("toast/show", {
        message: this.$t("_.reports.new_report_successfully_created")
      });
      this.$bus.$emit("refreshGeneratedReportsList");
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isProcessing = false;
    }
  }

  public updateFilters(filters) {
    this.filters = this.$_.omit(filters, ["limit", "offset"]);
  }

  // Computed

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  public get exampleRow() {
    return this.$_.get(this.reportHelp, "response[0]", {});
  }

  // Provide

  @Provide("reportProviderData")
  public providerData(): IReportProviderData {
    return {
      reportHelp: this.reportHelp,
      isLoading: this.isLoading,
      isProcessing: this.isProcessing,
      exampleRow: this.exampleRow,
      filters: this.filters
    };
  }

  @Provide("reportProviderMethods")
  public get providerMethods(): IReportProviderMethods {
    return {
      createReport: this.createReport,
      updateFilters: this.updateFilters
    };
  }

  // Render

  public render(h: CreateElement) {
    if (!this.contentIsAvailable)
      return h("guard", { props: { ifAdmin: false } });

    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true } });

    // Handle request error
    if (this.handleRequestError && this.requestError)
      return h("request-error", {
        props: { isLoading: this.isReloading && this.requestError },
        on: { click: () => this.reloadData() }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $reportData: this.providerData(),
        $reportMethods: this.providerMethods
      })
    );
  }
}
